import { Box, ListItem, ListItemButton, ListItemIcon, ListItemText, Popper, Grow, Paper, ClickAwayListener, MenuList } from "@mui/material";
import React, { useState } from "react";
import { boxShadows, colours } from "../../../StyleConstants";
import { useNavigate } from "react-router-dom";
import { SIDEBAR_FULL_WIDTH } from "../Sidebar";

type DropdownMenuButtonProps = {
    id: string;
    icon: any,
    label: string,
    menu?: MenuItemType[],
    selected: boolean,
    hoverTooltip?: string;
}

type MenuItemType = {
    label: string,
    path?: string,
    onClick?: () => void,
    hoverTooltip?: string;
}

const DropdownMenuItem = ({ item, closeMenu }: { item: MenuItemType, closeMenu: () => void }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const navigate = useNavigate();

    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMouseLeave = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <Box
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {item.hoverTooltip && (
                <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="right"
                >
                    <Paper sx={{ padding: 3, boxShadow: boxShadows.levelFive, marginLeft: 1 }}>
                        {item.hoverTooltip}
                    </Paper>
                </Popper>
            )}
            <ListItemButton
                onClick={() => {
                    if (item.onClick) {
                        item.onClick();
                    } else if (item.path) {
                        navigate(item.path);
                    }
                    closeMenu();
                }}
                id={`dropdown-sidebar-item-${item.label}`}
            >
                <ListItemText>
                    {item.label}
                </ListItemText>
            </ListItemButton>
        </Box>
    );
};

const DropdownSidebarItem = (props: DropdownMenuButtonProps) => {
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [tooltipAnchorEl, setTooltipAnchorEl] = React.useState<null | HTMLElement>(null);
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const open = Boolean(anchorRef.current);

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }

        setIsProfileMenuOpen(false);
    };
    const handleToggle = () => {
        setIsProfileMenuOpen((prevOpen) => !prevOpen);
    };
    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === "Tab") {
            event.preventDefault();
            setIsProfileMenuOpen(false);
        } else if (event.key === "Escape") {
            setIsProfileMenuOpen(false);
        }
    }

    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
        setTooltipAnchorEl(event.currentTarget);
    };

    const handleMouseLeave = () => {
        setTooltipAnchorEl(null);
    };

    const isTooltipOpen = Boolean(tooltipAnchorEl);
    const tooltipId = isTooltipOpen ? `tooltip-${props.id}` : undefined;

    return (
        <Box
            ref={anchorRef}
            component="li"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {props.hoverTooltip && (
                <Popper
                    id={tooltipId}
                    open={isTooltipOpen && !isProfileMenuOpen}
                    anchorEl={tooltipAnchorEl}
                    placement="right-end"
                >
                    <Paper sx={{ padding: 3, boxShadow: boxShadows.levelFive, marginLeft: 1 }}>
                        {props.hoverTooltip}
                    </Paper>
                </Popper>
            )}
            <ListItem
                disablePadding
                sx={{ display: 'block' }}
            >
                <ListItemButton
                    id={`dropdown-sidebar-item-${props.label}`}
                    aria-controls={open ? `${props.label} menu` : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleToggle}
                    sx={props.selected ? {
                        height: "40px",
                        backgroundColor: "#F9FAFC",
                        width: props.selected ? SIDEBAR_FULL_WIDTH + 8 : 208,
                        pr: 0,
                        borderRadius: "8px 0px 0px 8px",
                        "&:hover": {
                            backgroundColor: "#F9FAFC",
                        },
                    }
                        : {
                            height: "40px",
                            width: "208px",
                        }}
                >
                    <ListItemIcon>
                        <props.icon
                            sx={{
                                color: props.selected ? colours.orange.orange : 'white',
                                width: "1rem",
                                aspectRatio: 1,
                            }}
                        />
                    </ListItemIcon>
                    <ListItemText
                        disableTypography
                        primary={props.label}
                        // sx={{ marginLeft: "-1rem" }}
                        sx={{
                            color: props.selected ? colours.orange.orange : 'white',
                            marginLeft: "-1rem",
                            fontWeight: props.selected ? "600" : "400",
                        }}
                    />
                </ListItemButton>
                <Popper
                    open={isProfileMenuOpen}
                    anchorEl={anchorRef.current}
                    placement="right-start"
                    transition
                >
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                            <Paper>
                                <ClickAwayListener
                                    onClickAway={handleClose}
                                >
                                    <MenuList
                                        autoFocusItem={isProfileMenuOpen}
                                        id="composition-menu"
                                        aria-labelledby="composition-button"
                                        onKeyDown={handleListKeyDown}
                                        sx={{ color: colours.midnightBlue.blue160 }}
                                    >
                                        {props.menu?.map((item: MenuItemType) => (
                                            <DropdownMenuItem key={item.label} item={item} closeMenu={() => setIsProfileMenuOpen(false)} />
                                        ))}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </ListItem>
        </Box>

    )
}

export default DropdownSidebarItem;